import styled, { css } from 'styled-components'

import { color, radius, spacing, typography } from '../../themes'

interface ContainerProps {
  isOpen: boolean
  hasError: boolean
  disabled: boolean
  autoWidth: boolean
  bordered: boolean
}

export const Container = styled.div<ContainerProps>`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: ${({ autoWidth }) => (autoWidth ? 'auto' : '100%')};
  background: none;
  outline: 0;
  padding: 0;
  position: relative;
  ${({ bordered }) =>
    !bordered &&
    css`
      border-bottom: 1px solid ${color('gray.04')};
    `}

  &::after {
    content: '';
    transition: all 0.2s ease-in-out;
    position: absolute;
    display: block;
    top: 0;
    left: 0;
    bottom: 0;
    pointer-events: none;

    ${({ bordered }) =>
      !bordered
        ? css`
            right: 100%;
            border-bottom: 1px solid ${color('gray.04')};
          `
        : css`
            right: 0;
            border: 1px solid ${color('gray.04')};
            border-radius: ${radius('md')};
          `}
  }

  ${({ isOpen, bordered }) =>
    isOpen &&
    !bordered &&
    css`
      &::after {
        right: 0;
        border-bottom: 1px solid ${color('blue.01')};
      }
    `}

  ${({ hasError, bordered }) =>
    hasError &&
    css`
      &::after {
        ${!bordered
          ? css`
              right: 0;
              border-bottom: 1px solid ${color('red.01')};
            `
          : css`
              border: 1px solid ${color('red.01')};
            `}
      }
    `}

  ${({ disabled }) =>
    !disabled
      ? css`
          cursor: pointer;
        `
      : css`
          background: ${color('gray.05')};
        `}

  &:focus-within {
    &::after {
      ${({ hasError, bordered }) =>
        css`
          ${!bordered
            ? !hasError
              ? css`
                  right: 0;
                  border-bottom: 1px solid ${color('blue.01')};
                `
              : css`
                  right: 0;
                  border-bottom: 1px solid ${color('red.01')};
                `
            : !hasError
            ? css`
                border: 1px solid ${color('blue.01')};
              `
            : css`
                border: 1px solid ${color('red.01')};
              `}
        `}
    }
  }
`

const baseCss = css`
  ${typography('body1')};
  width: 100%;
  min-height: 58px;
  background: none;
  border: none;
  outline: none;
  padding: ${spacing(4, 3)};
  text-align: initial;
  display: block;
  color: ${color('gray.01')};
  box-sizing: border-box;
  position: relative;
`

export const InputContent = styled.input<{ hasNativeRightIcon: boolean }>`
  ${baseCss};

  &::placeholder {
    color: ${color('gray.04')};
  }

  &:disabled {
    color: ${color('gray.03')};

    &::placeholder {
      color: ${color('gray.03')};
    }
  }

  ${({ hasNativeRightIcon }) =>
    hasNativeRightIcon &&
    css`
      text-align: left;
      -webkit-appearance: textfield;
      -moz-appearance: textfield;

      &::-webkit-date-and-time-value {
        text-align: left;
        min-height: 26px;
      }

      &[type='time'] {
        &::-webkit-calendar-picker-indicator {
          // Colored clock icon (#ccc)
          background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMTEuOTkgMkM2LjQ3IDIgMiA2LjQ4IDIgMTJDMiAxNy41MiA2LjQ3IDIyIDExLjk5IDIyQzE3LjUyIDIyIDIyIDE3LjUyIDIyIDEyQzIyIDYuNDggMTcuNTIgMiAxMS45OSAyWk0xMiAyMEM3LjU4IDIwIDQgMTYuNDIgNCAxMkM0IDcuNTggNy41OCA0IDEyIDRDMTYuNDIgNCAyMCA3LjU4IDIwIDEyQzIwIDE2LjQyIDE2LjQyIDIwIDEyIDIwWk0xMi41IDdIMTFWMTNMMTYuMjUgMTYuMTVMMTcgMTQuOTJMMTIuNSAxMi4yNVY3WiIgZmlsbD0iI2NjYyIvPjwvc3ZnPg==');
          height: 24px;
          width: 24px;
          padding: 0;
        }
      }

      &[type='date'] {
        &::-webkit-calendar-picker-indicator {
          // Colored calendar icon (#ccc)
          background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMjAgM0gxOVYxSDE3VjNIN1YxSDVWM0g0QzIuOSAzIDIgMy45IDIgNVYyMUMyIDIyLjEgMi45IDIzIDQgMjNIMjBDMjEuMSAyMyAyMiAyMi4xIDIyIDIxVjVDMjIgMy45IDIxLjEgMyAyMCAzWk0yMCAyMUg0VjEwSDIwVjIxWk0yMCA4SDRWNUgyMFY4WiIgZmlsbD0iI2NjYyIvPjwvc3ZnPg==');
          height: 24px;
          width: 24px;
          padding: 0;
        }
      }
    `}
`

export const TextareaContent = styled.textarea`
  ${baseCss}
  min-height: 110px;
  padding: ${spacing(4)};

  &::placeholder {
    color: ${color('gray.04')};
  }

  &:disabled {
    color: ${color('gray.03')};

    &::placeholder {
      color: ${color('gray.03')};
    }
  }
`

interface ButtonContentProps {
  isPlaceholder: boolean
  disabled: boolean
}

export const ButtonContent = styled.div<ButtonContentProps>`
  ${baseCss}

  ${({ isPlaceholder }) =>
    isPlaceholder &&
    css`
      color: ${color('gray.04')};
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      color: ${color('gray.03')};
    `}
`

export const IconWrapper = styled.div<{ position: 'left' | 'right' }>`
  color: inherit;
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ position }) =>
    position === 'left' &&
    css`
      margin-right: ${spacing(2)};
    `}

  ${({ position }) =>
    position === 'right' &&
    css`
      margin-left: auto;
      padding-left: ${spacing(2)};
    `}
`
